import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ClearIcon from '@mui/icons-material/Clear';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material';
import validator from 'validator';

import TextInput from '~/components/ui/TextInput';
import { DESKTOP_MIN_WIDTH } from '~/constants/common';
import { VCardPlusEmail } from '~/types/qrCode';
import { isMaxLength } from '~/utils/common';

const iconButtonSx = { width: '40px', height: '40px' };

type VCardPlusContactInfoEmailItemProps = {
  email: VCardPlusEmail;
  updateVCardPlusEmail: (params: { body: any; emailId: number }) => void;
  isPendingDeleteVCardPlusEmail: boolean;
  deleteVCardPlusEmail: (emailId: number) => void;
};

export default function VCardPlusContactInfoEmailItem({
  email,
  isPendingDeleteVCardPlusEmail,
  updateVCardPlusEmail,
  deleteVCardPlusEmail,
}: VCardPlusContactInfoEmailItemProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const [emailTitle, setEmailTitle] = useState(email.title);
  const [emailValue, setEmailValue] = useState(email.email);

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="flex-end"
      mb={2}
      sx={{ flexDirection: isDesktop ? 'row' : 'column', gap: '16px' }}
    >
      <StyledSectionHeader>
        <TextInput
          value={emailTitle}
          onChange={(e) => setEmailTitle(e.target.value)}
          onBlur={() => updateVCardPlusEmail({ body: { title: emailTitle }, emailId: email.id })}
          inputProps={{ maxLength: 100 }}
          helperText={isMaxLength(emailTitle, 100)}
          fullWidth
          label={t('other:email')}
          placeholder={`${t('other:label')}...`}
          absoluteHelperText={isDesktop}
        />
        {!isDesktop && (
          <IconButton
            aria-label="delete"
            disabled={isPendingDeleteVCardPlusEmail}
            onClick={() => deleteVCardPlusEmail(email.id)}
            sx={iconButtonSx}
          >
            <ClearIcon />
          </IconButton>
        )}
      </StyledSectionHeader>
      <TextInput
        value={emailValue}
        onChange={(e) => setEmailValue(e.target.value)}
        onBlur={() => {
          if (validator.isEmail(emailValue, { ignore_max_length: true })) {
            updateVCardPlusEmail({ body: { email: emailValue }, emailId: email.id });
          }
        }}
        helperText={
          (!emailValue && t('inputs:required')) ||
          (!validator.isEmail(emailValue, { ignore_max_length: true }) && t('errors:emailIsValid')) ||
          isMaxLength(emailValue, 254)
        }
        inputProps={{ maxLength: 254 }}
        error={!validator.isEmail(emailValue, { ignore_max_length: true }) || !emailValue}
        fullWidth
        placeholder={t('settings:placeholdEmail')}
        absoluteHelperText={isDesktop}
        sx={{ flex: 1 }}
      />
      {isDesktop && (
        <IconButton
          aria-label="delete"
          disabled={isPendingDeleteVCardPlusEmail}
          onClick={() => deleteVCardPlusEmail(email.id)}
          sx={iconButtonSx}
        >
          <ClearIcon />
        </IconButton>
      )}
    </Box>
  );
}

export const StyledSectionHeader = styled('div')(({ theme }) => ({
  flex: 1,
  [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
    display: 'flex',
    alignItems: 'flex-end',
    width: '100%',
    gap: '16px',
  },
}));
